import type { TSidebarCollapsedSections } from "@frontend-shared/types";
import {
  isSidebarSectionOpen,
  readCollapsedSidebarSections,
  setCollapsedSidebarSections,
} from "@frontend-shared/utils";
import { useCallback, useMemo, useState } from "react";

import { localStorageAdapter } from "@/lib/storage-adapter";

interface IUseCollapsedSidebarSectionsProps {
  // NOTE: every key the calling accordion renders right now. Keys come and go
  // as pages load, which is exactly why the persisted record is merged rather
  // than overwritten — see setCollapsedSidebarSections.
  sectionKeys: string[];
}

interface IUseCollapsedSidebarSectionsResult {
  openSectionKeys: string[];
  handleOpenSectionsChange: (nextOpenKeys: string[]) => void;
}

// NOTE: NSAI-5926 — the sidebar's collapsible blocks used to forget what the
// user folded away the moment they reloaded. This backs a controlled Radix
// accordion with the shared localStorage record: state is seeded from storage
// on mount and written back on every toggle, so only the sections the user
// actually collapsed come back collapsed. A section the record has never heard
// of stays open, which keeps a date bucket or an assistant group that appears
// with the next page from rendering shut.
export const useCollapsedSidebarSections = ({
  sectionKeys,
}: IUseCollapsedSidebarSectionsProps): IUseCollapsedSidebarSectionsResult => {
  const [collapsedSections, setCollapsedSections] =
    useState<TSidebarCollapsedSections>(() =>
      readCollapsedSidebarSections({ storage: localStorageAdapter }),
    );

  const openSectionKeys = useMemo(
    () =>
      sectionKeys.filter((sectionKey) =>
        isSidebarSectionOpen({ collapsedSections, sectionKey }),
      ),
    [sectionKeys, collapsedSections],
  );

  const handleOpenSectionsChange = useCallback(
    (nextOpenKeys: string[]): void => {
      setCollapsedSections(
        setCollapsedSidebarSections({
          storage: localStorageAdapter,
          sectionKeys,
          collapsedSectionKeys: sectionKeys.filter(
            (sectionKey) => !nextOpenKeys.includes(sectionKey),
          ),
        }),
      );
    },
    [sectionKeys],
  );

  return { openSectionKeys, handleOpenSectionsChange };
};
